/**
 * Lightweight analytics client that posts events to our own backend.
 * No 3rd-party tracker. Session ID stored in localStorage.
 */

import { hasAnalyticsConsent } from "@/lib/consent";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API_EVENTS = `${BACKEND}/api/events`;
const SESSION_KEY = "ap_sid";
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 min idle session window

function ensureSessionId() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    const now = Date.now();
    if (raw) {
      const { sid, last } = JSON.parse(raw);
      if (sid && now - last < SESSION_TTL_MS) {
        localStorage.setItem(SESSION_KEY, JSON.stringify({ sid, last: now }));
        return sid;
      }
    }
    const sid = `s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem(SESSION_KEY, JSON.stringify({ sid, last: now }));
    return sid;
  } catch {
    return null;
  }
}

function parseUtm() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const out = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
    const v = p.get(k);
    if (v) out[k] = v;
  });
  return out;
}

/** Fire an event. Non-blocking. */
export function track(name, props = {}) {
  if (typeof window === "undefined" || !hasAnalyticsConsent()) return;
  try {
    const session_id = ensureSessionId();
    const path = window.location.pathname;
    const referrer = document.referrer || null;
    const token = localStorage.getItem("token") || null;
    const utm = parseUtm();
    const body = JSON.stringify({
      name,
      props: { ...utm, ...props },
      session_id,
      path,
      referrer,
    });
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    // sendBeacon doesn't support auth header; use fetch keepalive
    fetch(API_EVENTS, { method: "POST", headers, body, keepalive: true }).catch(() => {});
  } catch { /* swallow */ }
}

/** Track a page view. Call on each route change. */
export function trackPageView(extra = {}) {
  track("page_view", { ...extra });
}
